/**
 * Presentational atoms shared by every Help section (E13-F01).
 *
 * The guide is editorial content, not a data screen, so it needs a small set of
 * repeated shapes — a numbered section shell, a chip that renders a literal UI
 * label, step lists, bullet lists and callouts. Keeping them here means the 14
 * `sections/*.tsx` files contain nothing but the approved copy, and a change to
 * (say) the button-chip styling is one edit instead of fourteen.
 *
 * Every colour comes from the design.md token utilities (`bg-surface`,
 * `text-text-muted`, `text-accent`, …) — no literal hex (design.md → Do's and
 * Don'ts). The one intentional exception in this feature lives in `Sidebar.tsx`,
 * where the new Help control must match its literal-hex legacy-parity siblings.
 */
import type { ReactNode } from 'react';
import { helpSectionElementId, type HelpSectionMeta } from './help-sections.js';
import { helpShotUrl, type HelpShotSlug } from './help-shots.js';

/**
 * Resolves once every screenshot currently rendered in the guide has finished
 * loading (or failed) — scoped to Help's own `<img>`s via the `Shot` primitive's
 * `data-testid` prefix, not `document.images` at large (other, hidden views
 * stay mounted in the shell — see `AppShell`).
 *
 * `scrollToHelpSection` awaits this before measuring anything: an eager-loaded
 * but still in-flight image reserves no box (see `Shot`'s header) and grows the
 * page under the target the instant it finishes, so a scroll issued while any
 * earlier image is still loading computes its distance against a page shorter
 * than the one it is actually about to become, landing short.
 */
async function settledHelpShots(): Promise<void> {
  if (typeof document === 'undefined') {
    return;
  }
  const images = Array.from(
    document.querySelectorAll<HTMLImageElement>('[data-testid^="help-shot-"] img'),
  );
  await Promise.all(
    images.map(
      (img) =>
        img.complete ||
        new Promise<void>((resolve) => {
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => resolve(), { once: true });
        }),
    ),
  );
}

/**
 * Scroll a section into view and hand it keyboard focus.
 *
 * Used by the TOC and by in-copy cross-references, so "jump to a topic" behaves
 * identically wherever it is triggered. Deliberately NOT an `#anchor` link: the
 * shell's router owns `history` (path-based, no fragments), and a fragment
 * navigation would push a history entry the router does not model.
 *
 * `preventScroll` on focus() stops the browser from double-jumping past the
 * smooth scroll, and reduced-motion users get an instant jump. Async: waits
 * for `settledHelpShots()` first (see its comment) so the scroll target is
 * measured against the guide's FINAL layout, not a still-growing one.
 */
export async function scrollToHelpSection(id: string): Promise<void> {
  if (typeof document === 'undefined') {
    return;
  }
  await settledHelpShots();
  const element = document.getElementById(helpSectionElementId(id));
  if (!element) {
    return;
  }
  const reduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  element.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  element.focus({ preventScroll: true });
}

/** Inline cross-reference to another Help topic ("see the Clients section"). */
export function SectionLink({
  to,
  children,
}: {
  readonly to: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      data-testid={`help-link-${to}`}
      onClick={() => void scrollToHelpSection(to)}
      className="font-medium text-link underline decoration-link/40 underline-offset-2 hover:decoration-link"
    >
      {children}
    </button>
  );
}

/**
 * A literal UI label as it appears on screen — a button, tab, field or badge.
 * Rendered as a chip rather than bold text so «click **Approve**» is visually
 * unambiguous against surrounding emphasis.
 */
export function Btn({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return (
    <span className="mx-0.5 inline-block whitespace-nowrap rounded border border-border bg-surface-muted px-1.5 py-px align-baseline text-[0.9em] font-medium text-text-strong">
      {children}
    </span>
  );
}

/** A screen / area name (Pre-Kickoff, Project Portfolio) — lighter than `Btn`. */
export function Screen({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <strong className="font-semibold text-text-strong">{children}</strong>;
}

/** Body paragraph. */
export function P({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <p className="text-[15px] leading-relaxed text-text">{children}</p>;
}

/** A minor heading inside a section (the legacy guide's `h3`). */
export function SubHead({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return (
    <h3 className="brand-font text-base font-bold tracking-wide text-text-strong">{children}</h3>
  );
}

/** Numbered steps — gold numerals, the guide's "do this, then that" shape. */
export function Steps({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return (
    <ol className="list-outside list-decimal space-y-2.5 pl-5 text-[15px] leading-relaxed text-text marker:font-semibold marker:text-accent">
      {children}
    </ol>
  );
}

/** Bulleted list — gold markers, used for "what each thing is" enumerations. */
export function Bullets({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return (
    <ul className="list-outside list-disc space-y-2 pl-5 text-[15px] leading-relaxed text-text marker:text-accent">
      {children}
    </ul>
  );
}

/** A list row (works inside both `Steps` and `Bullets`). */
export function Li({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <li>{children}</li>;
}

/**
 * A highlighted aside. `note` = neutral context, `change` = «this moved / this
 * is different from what you remember», which is most of what changed between
 * the legacy guide and v2 and therefore deserves its own gold-flagged shape.
 */
export function Callout({
  tone = 'note',
  title,
  children,
}: {
  readonly tone?: 'note' | 'change';
  readonly title?: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  const change = tone === 'change';
  return (
    <aside
      className={`rounded-r-md border-l-4 py-3 pl-4 pr-4 text-[15px] leading-relaxed ${
        change ? 'border-accent bg-accent/8 text-text' : 'border-border bg-surface-muted text-text'
      }`}
    >
      {title ? (
        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">
          {title}
        </span>
      ) : null}
      {children}
    </aside>
  );
}

/**
 * A screenshot of the real v2 interface (E13-F01-T03).
 *
 * Renders NOTHING when the frame has not been captured yet — the acceptance
 * criterion is explicit that a section without a shot must render normally
 * rather than leave an empty `<figure>` (and `capture-help-screenshots.mjs` may
 * legitimately skip a frame when dev holds no suitable data for it).
 *
 * The image keeps its natural size up to the column width (`max-w-full`, no
 * `w-full`): the set mixes full-window frames (1280px wide) with cropped element
 * frames (the sidebar footer is 256px), and upscaling the small ones would blur
 * them.
 *
 * ── Deliberately NOT `loading="lazy"`.
 * These `<img>`s carry no `width`/`height` (the set's dimensions vary, per the
 * paragraph above), so the browser reserves no box for one until it loads —
 * lazy-loading them let each image pop in and grow the page while a reader (or
 * the TOC's `scrollIntoView`) was already scrolling past it, permanently
 * chasing a moving target. The 12 frames total ~600KB, trivial to load eagerly
 * on an internal single-page guide — correctness of "click a topic, land on
 * it" outweighs deferring images nobody scrolls fast enough to outrun anyway.
 *

 * The caption doubles as the `alt` text: it already describes what the frame
 * shows, and one string means the two can never drift apart.
 */
export function Shot({
  slug,
  caption,
}: {
  readonly slug: HelpShotSlug;
  readonly caption: string;
}): React.JSX.Element | null {
  const src = helpShotUrl(slug);
  if (src === undefined) {
    return null;
  }
  return (
    <figure
      data-testid={`help-shot-${slug}`}
      className="overflow-hidden rounded-md border border-border bg-surface-muted"
    >
      <img src={src} alt={caption} decoding="async" className="mx-auto block h-auto max-w-full" />
      <figcaption className="border-t border-border px-3 py-2 text-[13px] leading-snug text-text-muted">
        {caption}
      </figcaption>
    </figure>
  );
}

/**
 * The section shell: anchor id, e2e handle, numbered header, card body.
 *
 * `scroll-mt-6` gives the TOC's `scrollIntoView` a little breathing room under
 * the scroll container's top edge instead of butting the heading against it.
 */
export function HelpSectionCard({
  meta,
  children,
}: {
  readonly meta: HelpSectionMeta;
  readonly children: ReactNode;
}): React.JSX.Element {
  const elementId = helpSectionElementId(meta.id);
  return (
    <section
      id={elementId}
      data-testid={elementId}
      data-help-section={meta.id}
      aria-labelledby={`${elementId}-heading`}
      tabIndex={-1}
      className="scroll-mt-6 rounded-lg border border-border bg-surface p-6 outline-none sm:p-7"
    >
      <header className="mb-4 flex items-center gap-3 border-b border-border pb-3">
        {meta.number === undefined ? null : (
          <span
            aria-hidden
            className="brand-font flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary text-sm font-bold text-accent"
          >
            {meta.number}
          </span>
        )}
        <h2
          id={`${elementId}-heading`}
          className="brand-font text-xl font-bold leading-tight text-text-strong"
        >
          {meta.title}
        </h2>
      </header>
      <div className="space-y-4">{children}</div>
    </section>
  );
}
